import MentionMenuHeader from "app/components/ReplyComment/components/MentionDropdown/components/MentionMenuHeader";
import { useMentionDropdown } from "app/components/ReplyComment/components/MentionDropdown/useMentionDropdown";
import { selectMentionData } from "app/components/ReplyComment/redux/selectors";
import { replyCommentSliceActions } from "app/components/ReplyComment/redux/slice";
import { TicketType } from "app/components/ReplyComment/redux/types";
import {
  MentionDropdownComponentProps,
  MentionType,
} from "app/components/TextEditor/components/mentionSuggestion";
import { IMemberList } from "app/containers/Features/components/MemberSelection/types";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RenderMemberItems from "./components/RenderMemberItems";
import RenderTicketItems from "./components/RenderTicketItems";
import { TabType } from "./types";

const MentionDropdown: FC<
  MentionDropdownComponentProps & {
    projectId?: string;
    teamId?: string;
    showMemberMenu?: boolean;
  }
> = ({ command, projectId, teamId, showMemberMenu }) => {
  // Hooks
  const dispatch = useDispatch();

  // Global State
  const mentionData = useSelector(selectMentionData);

  // States
  const [activeTab, setActiveTab] = useState<TabType>(TabType.Members);
  const [searchQuery, setSearchQuery] = useState("");
  const { filteredMembers, handleTicketScroll, ticketLoading, memberLoading } =
    useMentionDropdown(searchQuery, projectId as string);

  // Functions
  const handleMemberSelect = (member: IMemberList) => {
    command({ id: member.userId, label: member.name, type: MentionType.USER });
  };

  const handleMemberMenuClick = (memberId?: string) => () => {
    if (!memberId) return;

    const selectedMember = filteredMembers.find(
      (member) => member._id === memberId,
    );
    if (!selectedMember) return;

    command({
      id: selectedMember.userId,
      label: selectedMember.name,
      type: MentionType.USER,
    });
  };

  const handleTicketSelect = (ticket: TicketType) => {
    command({
      id: ticket._id,
      label:
        ticket.ticketName && ticket.ticketID
          ? `${ticket.ticketID} ${ticket.ticketName}`
          : ticket.ticketName || ticket.ticketID,
      type: MentionType.TICKET,
      ticketTitle: ticket.ticketName,
      ticketId: ticket.ticketID,
      ticketTypeName: ticket.type.name,
      ticketTypeIcon: ticket.type.icon,
      ticketStatusName: ticket.status.name,
      ticketStatusColor: ticket.status.color,
      ticketPriority: ticket.priority || "",
      projectId: projectId,
      teamId: teamId,
    });
  };

  // Functions
  const getMentionMemberData = () => {
    if (!projectId) return;

    if (mentionData[projectId]) return;

    dispatch(
      replyCommentSliceActions.fetchProjectMembersList({
        projectId,
      }),
    );
  };

  const getMentionTicketData = () => {
    if (!projectId) return;

    if (mentionData[projectId]?.tickets?.list?.length) return;

    dispatch(
      replyCommentSliceActions.fetchTicketsRequest({
        projectId,
        query: searchQuery,
      }),
    );
  };

  // Effects
  useEffect(() => {
    getMentionMemberData();
  }, [projectId, dispatch]);

  useEffect(() => {
    getMentionTicketData();
  }, [projectId, dispatch, searchQuery]);

  return (
    <div className="flex flex-col gap-0 w-[445px] bg-white rounded-[4px] border border-gray-100 z-50 drop-shadow-lg overflow-hidden">
      <MentionMenuHeader
        showMemberMenu={showMemberMenu}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        searchQuery={searchQuery}
        handleMemberMenuClick={handleMemberMenuClick}
        onSearchChange={(query) => {
          setSearchQuery(query);
          if (query.length) {
            dispatch(
              replyCommentSliceActions.clearTicketData({
                projectId: projectId as string,
              }),
            );
          }
        }}
      />

      {activeTab === TabType.Members && (
        <RenderMemberItems
          handleMemberSelect={handleMemberSelect}
          filteredMembers={filteredMembers}
          memberLoading={memberLoading}
          projectId={projectId as string}
        />
      )}

      {activeTab === TabType.Tickets && (
        <RenderTicketItems
          handleTicketScroll={handleTicketScroll}
          ticketLoading={ticketLoading}
          projectId={projectId as string}
          handleTicketSelect={handleTicketSelect}
        />
      )}
    </div>
  );
};

export default MentionDropdown;
